import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faPlus, faCheck } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import apiServices from '../../../services/apiService';
import storageService from '../../../utils/storageService';

const PREDEFINED_ACTIONS = ['add', 'view', 'edit', 'delete'];

const AddMasterPermissionModal = ({ isOpen, onClose, onSuccess, modules }) => {
    // Step 1: Module
    const [selectedModule, setSelectedModule] = useState('');
    const [isNewModule, setIsNewModule] = useState(false);
    const [newModuleName, setNewModuleName] = useState('');

    // Step 2: Sub-module
    const [selectedSubModule, setSelectedSubModule] = useState('');
    const [isNewSubModule, setIsNewSubModule] = useState(false);
    const [newSubModuleName, setNewSubModuleName] = useState('');

    // Step 3: Action
    const [action, setAction] = useState('');
    const [isCustomAction, setIsCustomAction] = useState(false);
    const [customAction, setCustomAction] = useState('');

    // Description
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reset state on open
    useEffect(() => {
        if (isOpen) {
            setSelectedModule('');
            setIsNewModule(false);
            setNewModuleName('');
            setSelectedSubModule('');
            setIsNewSubModule(false);
            setNewSubModuleName('');
            setAction('');
            setIsCustomAction(false);
            setCustomAction('');
            setDescription('');
        }
    }, [isOpen]);

    // Compute existing submodules dynamically from the selected module
    const availableSubModules = useMemo(() => {
        if (isNewModule || !selectedModule) return [];
        const mod = (modules || []).find(m => m.moduleName === selectedModule);
        if (!mod || !mod.features) return [];

        const subs = new Set();
        mod.features.forEach(f => {
            if (f.permissionKey) {
                const parts = f.permissionKey.split(':');
                if (parts.length >= 2) {
                    subs.add(parts[1]); // e.g. "gatepass:settings:edit" -> "settings"
                }
            }
        });
        return Array.from(subs);
    }, [selectedModule, modules, isNewModule]);

    // Helper to format string for the key
    const sanitizeKeyPart = (str) => {
        return (str || '').toLowerCase().replace(/[^a-z_]/g, '_');
    };

    const finalModuleName = isNewModule ? newModuleName : selectedModule;
    
    const previewKey = useMemo(() => {
        let m = sanitizeKeyPart(finalModuleName);
        let sm = isNewSubModule ? sanitizeKeyPart(newSubModuleName) : sanitizeKeyPart(selectedSubModule);
        let act = isCustomAction ? sanitizeKeyPart(customAction) : sanitizeKeyPart(action);

        let parts = [];
        if (m) parts.push(m);
        if (sm) parts.push(sm);
        if (act) parts.push(act);

        return parts.join(':');
    }, [finalModuleName, selectedSubModule, isNewSubModule, newSubModuleName, action, isCustomAction, customAction]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const sm = isNewSubModule ? newSubModuleName : selectedSubModule;
        const act = isCustomAction ? customAction : action;

        if (!finalModuleName.trim() || !sm.trim() || !act.trim() || !description.trim()) {
            toast.error("Please fill in all fields.");
            return;
        }

        const regex = /^[a-z_]+:[a-z_]+:[a-z_]+$/;
        if (!regex.test(previewKey)) {
            toast.error("Generated key is invalid. Must be exactly 'module:submodule:action' format.");
            return;
        }

        setIsSubmitting(true);
        try {
            let employeeCode = storageService.getItem("employeeCode") || 1; 

            const payload = {
                permissionKey: previewKey,
                description: description,
                moduleName: finalModuleName,
                createdBy: parseInt(employeeCode) || 1,
                isActive: 1
            };

            const response = await apiServices({
                method: "POST",
                url: `/Permissions/master`,
                data: payload
            });

            const responseData = response.data || {};
            const isBackendSuccess = responseData.isSuccess;
            const msg = responseData.message || response.message;

            if (response.success && isBackendSuccess) {
                toast.success(msg || "Master permission created successfully");
                onSuccess(); 
                onClose();
            } else if (msg && msg.includes("String '1' was not recognized as a valid Boolean")) {
                // Workaround: Backend SP succeeded but C# threw exception on "1"
                toast.success("Master permission created successfully");
                onSuccess(); 
                onClose();
            } else if (msg && msg.includes("String '0' was not recognized as a valid Boolean")) {
                // Workaround: Backend SP returned "0" (duplicate key/failure) and C# threw exception
                toast.error("Permission Key already exists or validation failed.");
            } else {
                toast.error(msg || "Failed to create master permission");
            }
        } catch (error) {
            console.error("Error creating master permission:", error);
            toast.error("An error occurred while creating the permission.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm px-4">
            <div className="bg-white dark:bg-[#1A1A1D] w-full max-w-lg rounded-2xl shadow-xl overflow-hidden animate-fade-in-up flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-[#2C2C2E] shrink-0">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        Permission Builder
                    </h3>
                    <button 
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors w-8 h-8 flex justify-center items-center rounded-full hover:bg-gray-100 dark:hover:bg-[#252528]"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Scrollable Body */}
                <div className="p-6 overflow-y-auto flex-1">
                    <form id="permission-form" onSubmit={handleSubmit} className="flex flex-col gap-6">
                        
                        {/* 1. Module Selection */}
                        <div className="bg-gray-50 dark:bg-[#202022] p-4 rounded-xl border border-gray-100 dark:border-gray-800">
                            <div className="flex justify-between items-center mb-2">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                    1. Module <span className="text-red-500">*</span>
                                </label>
                                <button 
                                    type="button" 
                                    onClick={() => {
                                        setIsNewModule(!isNewModule);
                                        setSelectedModule('');
                                        setNewModuleName('');
                                        setSelectedSubModule('');
                                        setIsNewSubModule(false);
                                    }}
                                    className="text-xs text-primaryaccent font-semibold hover:underline"
                                >
                                    {isNewModule ? 'Select Existing' : '+ Add New'}
                                </button>
                            </div>
                            
                            {isNewModule ? (
                                <input
                                    type="text"
                                    value={newModuleName}
                                    onChange={e => setNewModuleName(e.target.value)}
                                    placeholder="e.g., GatePass"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm outline-none bg-white dark:bg-[#1A1A1D] text-gray-800 dark:text-white focus:border-primaryaccent transition-colors"
                                />
                            ) : (
                                <select
                                    value={selectedModule}
                                    onChange={e => {
                                        setSelectedModule(e.target.value);
                                        setSelectedSubModule('');
                                        setIsNewSubModule(false);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm outline-none bg-white dark:bg-[#1A1A1D] text-gray-800 dark:text-white focus:border-primaryaccent transition-colors"
                                >
                                    <option value="">-- Select Module --</option>
                                    {(modules || []).map((m, idx) => (
                                        <option key={idx} value={m.moduleName}>{m.moduleName}</option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {/* 2. Sub-module Selection */}
                        <div className={`p-4 rounded-xl border transition-all ${!finalModuleName ? 'opacity-50 pointer-events-none bg-gray-50 dark:bg-[#202022] border-gray-100 dark:border-gray-800' : 'bg-gray-50 dark:bg-[#202022] border-gray-100 dark:border-gray-800'}`}>
                            <div className="flex justify-between items-center mb-2">
                                <label className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                    2. Sub-module <span className="text-red-500">*</span>
                                </label>
                                <button 
                                    type="button" 
                                    onClick={() => {
                                        setIsNewSubModule(!isNewSubModule);
                                        setSelectedSubModule('');
                                        setNewSubModuleName('');
                                    }}
                                    className="text-xs text-primaryaccent font-semibold hover:underline"
                                >
                                    {isNewSubModule ? 'Select Existing' : '+ Add New'}
                                </button>
                            </div>

                            {isNewSubModule || availableSubModules.length === 0 ? (
                                <div>
                                    <input
                                        type="text"
                                        value={newSubModuleName}
                                        onChange={e => setNewSubModuleName(e.target.value)}
                                        placeholder="e.g., settings"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm outline-none bg-white dark:bg-[#1A1A1D] text-gray-800 dark:text-white focus:border-primaryaccent transition-colors"
                                    />
                                    {availableSubModules.length === 0 && !isNewModule && selectedModule && !isNewSubModule && (
                                        <p className="text-xs text-gray-500 mt-1 italic">No existing sub-modules found. Type a new one.</p>
                                    )}
                                </div>
                            ) : (
                                <select
                                    value={selectedSubModule}
                                    onChange={e => setSelectedSubModule(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm outline-none bg-white dark:bg-[#1A1A1D] text-gray-800 dark:text-white focus:border-primaryaccent transition-colors"
                                >
                                    <option value="">-- Select Sub-module --</option>
                                    {availableSubModules.map((sm, idx) => (
                                        <option key={idx} value={sm}>{sm}</option>
                                    ))}
                                </select>
                            )}
                        </div>

                        {/* 3. Action */}
                        <div className={`p-4 rounded-xl border transition-all ${(!finalModuleName || (!selectedSubModule && !newSubModuleName)) ? 'opacity-50 pointer-events-none bg-gray-50 dark:bg-[#202022] border-gray-100 dark:border-gray-800' : 'bg-gray-50 dark:bg-[#202022] border-gray-100 dark:border-gray-800'}`}>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-200 mb-3">
                                3. Action <span className="text-red-500">*</span>
                            </label>
                            
                            <div className="flex flex-wrap gap-2 mb-3">
                                {PREDEFINED_ACTIONS.map(act => (
                                    <button
                                        key={act}
                                        type="button"
                                        onClick={() => {
                                            setIsCustomAction(false);
                                            setAction(act);
                                        }}
                                        className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors border ${
                                            !isCustomAction && action === act 
                                            ? 'bg-primaryaccent border-primaryaccent text-white' 
                                            : 'bg-white dark:bg-[#1A1A1D] border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                        }`}
                                    >
                                        {(!isCustomAction && action === act) && <FontAwesomeIcon icon={faCheck} className="mr-1.5" />}
                                        {act.charAt(0).toUpperCase() + act.slice(1)}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsCustomAction(true);
                                        setAction('');
                                    }}
                                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors border ${
                                        isCustomAction
                                        ? 'bg-primaryaccent border-primaryaccent text-white' 
                                        : 'bg-white dark:bg-[#1A1A1D] border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                    }`}
                                >
                                    {isCustomAction && <FontAwesomeIcon icon={faCheck} className="mr-1.5" />}
                                    Custom
                                </button>
                            </div>

                            {isCustomAction && (
                                <input
                                    type="text"
                                    value={customAction}
                                    onChange={e => setCustomAction(e.target.value)}
                                    placeholder="e.g., approve, export"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm outline-none bg-white dark:bg-[#1A1A1D] text-gray-800 dark:text-white focus:border-primaryaccent transition-colors"
                                />
                            )}
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                Description <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="Allows the user to..."
                                rows="2"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-bordercolor rounded-lg text-sm outline-none bg-gray-50 dark:bg-[#202022] text-gray-800 dark:text-white focus:border-primaryaccent transition-colors resize-none"
                            ></textarea>
                        </div>

                    </form>
                </div>

                {/* Sticky Footer: Preview & Actions */}
                <div className="bg-gray-100 dark:bg-[#151515] p-5 border-t border-gray-200 dark:border-[#2C2C2E] shrink-0">
                    <div className="mb-4">
                        <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                            Generated Permission Key Preview
                        </p>
                        <div className="bg-gray-800 dark:bg-black text-green-400 font-mono text-sm px-4 py-3 rounded-lg shadow-inner overflow-x-auto whitespace-nowrap">
                            {previewKey || 'module:submodule:action'}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 font-bold bg-white dark:bg-[#252528] hover:bg-gray-50 dark:hover:bg-[#2A2A2D] text-xs transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="permission-form"
                            disabled={isSubmitting || previewKey.split(':').length !== 3 || !description.trim()}
                            className="px-6 py-2 rounded-lg text-white font-bold bg-primaryaccent hover:bg-opacity-90 disabled:opacity-50 text-xs flex items-center justify-center gap-2 transition-colors min-w-[120px]"
                        >
                            {isSubmitting ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                    Saving...
                                </>
                            ) : 'Create Permission'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddMasterPermissionModal;
